import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Text, Flex, Field } from '@chakra-ui/react';
import { DrawerComponent } from '../../ui/drawerComponent';
import {
  Button as ButtonComponent,
  InputField,
  TextareaField,
  ToggleGroup,
  type ToggleOption,
} from '@themes/aegix';
import { FiEdit } from 'react-icons/fi';
import { RiCloseCircleLine } from 'react-icons/ri';
import { HiPlus } from 'react-icons/hi2';
import { MdTextFields, MdRadioButtonChecked, MdCheckBox } from 'react-icons/md';
import {
  REPORT_STEP_PHASES,
  reportStepPhaseLabels,
  type ReportStepPhase,
  type ReportStepParameter,
} from '../../../services';

export type ReportStepFieldType = 'text' | 'single_select' | 'multi_select';

export interface ReportStepOption {
  value: string;
  label: string;
}

export interface ReportStepLocal {
  id?: number;
  /** ADR 143 §1 — which form this question belongs to. Never optional. */
  phase: ReportStepPhase;
  field_type: ReportStepFieldType;
  label: string;
  field_slug?: string;
  placeholder?: string;
  description?: string;
  options?: ReportStepOption[];
  order: number;
  is_required: boolean;
  allow_other?: boolean;
  parameter?: ReportStepParameter | null;
  _isNew?: boolean;
  _isDeleted?: boolean;
}

export const slugifyLabel = (text: string): string =>
  text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');

const fieldTypeIcons: Record<ReportStepFieldType, React.ReactNode> = {
  text: <MdTextFields size={16} />,
  single_select: <MdRadioButtonChecked size={16} />,
  multi_select: <MdCheckBox size={16} />,
};

interface ProtocolReportStepEditorProps {
  steps: ReportStepLocal[];
  onStepsChange: (steps: ReportStepLocal[]) => void;
}

export function ProtocolReportStepEditor({
  steps,
  onStepsChange,
}: ProtocolReportStepEditorProps) {
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<ReportStepLocal | null>(null);

  const [phase, setPhase] = useState<ReportStepPhase>('intake');
  const [fieldType, setFieldType] = useState<ReportStepFieldType>('text');
  const [label, setLabel] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const [description, setDescription] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [allowOther, setAllowOther] = useState(false);
  const [stepOptions, setStepOptions] = useState<ReportStepOption[]>([]);

  // ADR-144 D3 Parameter declaration state (intake phase only)
  const [paramAnnounce, setParamAnnounce] = useState(false);
  const [paramAnnounceOrder, setParamAnnounceOrder] = useState<number>(1);
  const [paramRequiredAtCreate, setParamRequiredAtCreate] = useState<
    'require' | 'prompt' | 'none'
  >('none');

  const fieldTypeLabels: Record<ReportStepFieldType, string> = useMemo(
    () => ({
      text: t('protocols.fieldTypeTextInput', 'Text Input'),
      single_select: t('protocols.fieldTypeSingleSelect', 'Single Select'),
      multi_select: t('protocols.fieldTypeMultiSelect', 'Multi Select'),
    }),
    [t],
  );

  const phaseDescriptions: Record<ReportStepPhase, string> = useMemo(
    () => ({
      intake: t(
        'protocols.phaseDescriptionIntake',
        'Asked of the person starting the alert or incident.',
      ),
      end_event_survey: t(
        'protocols.phaseDescriptionEndEventSurvey',
        'Asked of recipients once the event has ended.',
      ),
    }),
    [t],
  );

  const activeSteps = steps.filter((s) => !s._isDeleted);

  const stepsInPhase = (target: ReportStepPhase) =>
    activeSteps
      .filter((s) => s.phase === target)
      .sort((a, b) => a.order - b.order);

  const resetForm = () => {
    setPhase('intake');
    setFieldType('text');
    setLabel('');
    setPlaceholder('');
    setDescription('');
    setIsRequired(false);
    setAllowOther(false);
    setStepOptions([]);
    setParamAnnounce(false);
    setParamAnnounceOrder(1);
    setParamRequiredAtCreate('none');
    setEditingStep(null);
  };

  const handleAddStep = (initialPhase: ReportStepPhase = 'intake') => {
    resetForm();
    setPhase(initialPhase);
    setIsDrawerOpen(true);
  };

  const handleEditStep = (s: ReportStepLocal) => {
    // Seed every control from the step, `phase` included — an edit must never
    // silently rewrite the phase to the form default.
    setPhase(s.phase);
    setFieldType(s.field_type);
    setLabel(s.label);
    setPlaceholder(s.placeholder ?? '');
    setDescription(s.description ?? '');
    setIsRequired(s.is_required);
    setAllowOther(s.allow_other ?? false);
    setStepOptions(s.options ?? []);
    setParamAnnounce(s.parameter?.announce ?? false);
    setParamAnnounceOrder(s.parameter?.announce_order ?? 1);
    setParamRequiredAtCreate(s.parameter?.required_at_create ?? 'none');
    setEditingStep(s);
    setIsDrawerOpen(true);
  };

  const handleDeleteStep = (s: ReportStepLocal) => {
    const origIdx = steps.findIndex((os) => os === s);
    if (origIdx === -1) return;
    if (s.id) {
      const updated = [...steps];
      updated[origIdx] = { ...s, _isDeleted: true };
      onStepsChange(updated);
    } else {
      onStepsChange(steps.filter((_, i) => i !== origIdx));
    }
  };

  const handleSaveStep = () => {
    if (!label.trim()) return;

    const isSelectType = fieldType !== 'text';
    const keepsPhase = editingStep !== null && editingStep.phase === phase;
    const order = keepsPhase
      ? editingStep.order
      : stepsInPhase(phase).filter((s) => s !== editingStep).length;

    const slug = editingStep?.field_slug || slugifyLabel(label);
    const parameter: ReportStepParameter | undefined =
      phase === 'intake'
        ? {
            announce: paramAnnounce,
            announce_order: Number(paramAnnounceOrder) || 1,
            required_at_create: paramRequiredAtCreate,
          }
        : undefined;

    const stepData: Omit<ReportStepLocal, 'id' | '_isNew' | '_isDeleted'> = {
      phase,
      field_type: fieldType,
      label: label.trim(),
      field_slug: slug,
      placeholder:
        fieldType === 'text' ? placeholder.trim() || undefined : undefined,
      description: description.trim() || undefined,
      options: isSelectType ? stepOptions : undefined,
      order,
      is_required: isRequired,
      allow_other: isSelectType ? allowOther : undefined,
      parameter,
    };

    if (editingStep !== null) {
      const origIdx = steps.findIndex((os) => os === editingStep);
      const updated = [...steps];
      updated[origIdx] = { ...editingStep, ...stepData };
      onStepsChange(updated);
    } else {
      onStepsChange([...steps, { ...stepData, _isNew: true }]);
    }

    setIsDrawerOpen(false);
    resetForm();
  };

  const handleFieldTypeChange = (newType: ReportStepFieldType) => {
    setFieldType(newType);
    if (newType !== 'text' && stepOptions.length === 0) {
      setStepOptions([{ value: '', label: '' }]);
    }
    if (newType === 'text') {
      setStepOptions([]);
      setAllowOther(false);
    }
  };

  const handleAddOption = () => {
    setStepOptions([...stepOptions, { value: '', label: '' }]);
  };

  const handleUpdateOption = (
    idx: number,
    field: 'label' | 'value',
    val: string,
  ) => {
    const updated = [...stepOptions];
    if (field === 'label') {
      updated[idx] = {
        ...updated[idx],
        label: val,
        value: val.toLowerCase().replace(/\s+/g, '_'),
      };
    } else {
      updated[idx] = { ...updated[idx], value: val };
    }
    setStepOptions(updated);
  };

  const handleRemoveOption = (idx: number) => {
    setStepOptions(stepOptions.filter((_, i) => i !== idx));
  };

  const isSelectType = fieldType !== 'text';

  return (
    <Box>
      {activeSteps.length === 0 ? (
        <Text fontSize="sm" color="text.secondary" mb={3}>
          {t('protocols.noReportFieldsDefined', 'No report fields defined.')}
        </Text>
      ) : (
        <Box mb={3}>
          {REPORT_STEP_PHASES.map((groupPhase) => {
            const groupSteps = stepsInPhase(groupPhase);
            if (groupSteps.length === 0) return null;

            return (
              <Box
                key={groupPhase}
                mb={4}
                data-e2e={`report-field-group-${groupPhase}`}
              >
                <Flex alignItems="baseline" gap={2} mb={1}>
                  <Text
                    fontSize="xs"
                    fontWeight={700}
                    color="text.secondary"
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    {reportStepPhaseLabels[groupPhase]}
                  </Text>
                  <Text fontSize="xs" color="text.secondary">
                    {phaseDescriptions[groupPhase]}
                  </Text>
                </Flex>
                {groupSteps.map((step, i) => (
                  <Flex
                    key={step.id ?? `rs-${groupPhase}-${i}`}
                    alignItems="center"
                    justifyContent="space-between"
                    px={3}
                    py={2}
                    mb={1}
                    bg="surface.secondary"
                    borderRadius="md"
                    data-e2e={`report-field-row-${groupPhase}-${i}`}
                  >
                    <Flex alignItems="center" gap={2}>
                      {fieldTypeIcons[step.field_type]}
                      <Text fontSize="sm" color="text.primary">
                        {step.label}
                        {step.is_required && (
                          <Text as="span" color="text.danger" ml={0.5}>
                            *
                          </Text>
                        )}
                      </Text>
                    </Flex>
                    <Flex gap={1}>
                      <Box
                        as="button"
                        p={1}
                        _hover={{ color: 'brand.500' }}
                        onClick={() => handleEditStep(step)}
                        data-e2e={`edit-report-field-button-${groupPhase}-${i}`}
                      >
                        <FiEdit size={12} />
                      </Box>
                      <Box
                        as="button"
                        p={1}
                        color="text.danger"
                        _hover={{ color: 'red.600' }}
                        onClick={() => handleDeleteStep(step)}
                        data-e2e={`delete-report-field-button-${groupPhase}-${i}`}
                      >
                        <RiCloseCircleLine size={14} />
                      </Box>
                    </Flex>
                  </Flex>
                ))}
              </Box>
            );
          })}
        </Box>
      )}

      <Flex gap={2} flexWrap="wrap">
        {REPORT_STEP_PHASES.map((addPhase) => (
          <ButtonComponent
            key={addPhase}
            visual="secondary"
            size="sm"
            onClick={() => handleAddStep(addPhase)}
            data-e2e={
              addPhase === 'intake'
                ? 'add-report-field-button'
                : `add-report-field-button-${addPhase}`
            }
          >
            <HiPlus size={14} />
            <Text ml={1}>
              {t('protocols.addPhaseField', 'Add {{phase}} Field', {
                phase: reportStepPhaseLabels[addPhase],
              })}
            </Text>
          </ButtonComponent>
        ))}
      </Flex>

      {/* Step edit drawer */}
      <DrawerComponent
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          resetForm();
        }}
        title={
          editingStep !== null
            ? t('protocols.editReportField', 'Edit Report Field')
            : t('protocols.addReportField', 'Add Report Field')
        }
        size="md"
        e2eTitle="report-field-edit-drawer"
        footer={
          <Flex gap={3} width="100%">
            <ButtonComponent
              visual="secondary"
              onClick={() => {
                setIsDrawerOpen(false);
                resetForm();
              }}
              flex={1}
              data-e2e="cancel-button"
            >
              {t('common.cancel', 'Cancel')}
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              onClick={handleSaveStep}
              flex={1}
              data-e2e="editindex--null--save--add-button"
            >
              {editingStep !== null
                ? t('common.save', 'Save')
                : t('common.add', 'Add')}
            </ButtonComponent>
          </Flex>
        }
      >
        <Box>
          <Text fontSize="sm" fontWeight={600} color="text.primary" mb={1}>
            {t('protocols.phase', 'Phase')}
          </Text>
          <ToggleGroup
            options={REPORT_STEP_PHASES.map<ToggleOption>((option) => ({
              value: option,
              label: reportStepPhaseLabels[option],
              'data-e2e': `report-field-phase-option-${option}`,
            }))}
            value={phase}
            onChange={(v) => setPhase(v as ReportStepPhase)}
            fillWidth
            data-e2e="report-field-phase-toggle-group"
          />
          <Text fontSize="xs" color="text.secondary" mt={1} mb={4}>
            {phaseDescriptions[phase]}
          </Text>

          <Text fontSize="sm" fontWeight={600} color="text.primary" mb={1}>
            {t('protocols.fieldType', 'Field Type')}
          </Text>
          <Flex gap={2} mb={4} flexWrap="wrap">
            {(Object.keys(fieldTypeLabels) as ReportStepFieldType[]).map(
              (type) => (
                <Box
                  key={type}
                  px={3}
                  py={1.5}
                  borderRadius="md"
                  cursor="pointer"
                  fontSize="sm"
                  fontWeight={500}
                  bg={fieldType === type ? 'surface.tertiary' : 'transparent'}
                  border="1px solid"
                  borderColor={
                    fieldType === type ? 'border.secondary' : 'border.primary'
                  }
                  color="text.primary"
                  onClick={() => handleFieldTypeChange(type)}
                  data-e2e={`field-type-option-${type}`}
                >
                  <Flex alignItems="center" gap={1}>
                    {fieldTypeIcons[type]}
                    {fieldTypeLabels[type]}
                  </Flex>
                </Box>
              ),
            )}
          </Flex>

          <Field.Root mb={4}>
            <Field.Label htmlFor="report-field-label-input">
              {t('common.label', 'Label')}
            </Field.Label>
            <InputField
              id="report-field-label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t('protocols.fieldLabelPlaceholder', 'Field label')}
              data-e2e="report-field-label-input"
            />
          </Field.Root>

          {fieldType === 'text' && (
            <>
              <Field.Root mb={4}>
                <Field.Label htmlFor="report-field-placeholder-input">
                  {t('protocols.placeholder', 'Placeholder')}
                </Field.Label>
                <InputField
                  id="report-field-placeholder-input"
                  value={placeholder}
                  onChange={(e) => setPlaceholder(e.target.value)}
                  placeholder={t(
                    'protocols.placeholderTextPlaceholder',
                    'Placeholder text',
                  )}
                  data-e2e="report-field-placeholder-input"
                />
              </Field.Root>
            </>
          )}

          <Field.Root mb={4}>
            <Field.Label htmlFor="report-field-help-text-textarea">
              {t('protocols.helpText', 'Help Text')}
            </Field.Label>
            <TextareaField
              id="report-field-help-text-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t(
                'protocols.optionalHelpTextPlaceholder',
                'Optional help text',
              )}
              minH="60px"
              data-e2e="report-field-help-text-textarea"
            />
          </Field.Root>

          <Flex
            alignItems="center"
            gap={2}
            cursor="pointer"
            mb={4}
            onClick={() => setIsRequired(!isRequired)}
          >
            <Box
              w="16px"
              h="16px"
              borderRadius="sm"
              border="2px solid"
              borderColor={isRequired ? 'brand.500' : 'border.secondary'}
              bg={isRequired ? 'brand.500' : 'transparent'}
            />
            <Text
              fontSize="sm"
              color="text.primary"
              data-e2e="report-field-required-checkbox"
            >
              {t('protocols.requiredField', 'Required field')}
            </Text>
          </Flex>

          {isSelectType && (
            <>
              <Flex
                alignItems="center"
                gap={2}
                cursor="pointer"
                mb={4}
                onClick={() => setAllowOther(!allowOther)}
              >
                <Box
                  w="16px"
                  h="16px"
                  borderRadius="sm"
                  border="2px solid"
                  borderColor={allowOther ? 'brand.500' : 'border.secondary'}
                  bg={allowOther ? 'brand.500' : 'transparent'}
                />
                <Text
                  fontSize="sm"
                  color="text.primary"
                  data-e2e="report-field-allow-other-checkbox"
                >
                  {t('protocols.allowOtherOption', 'Allow "Other" option')}
                </Text>
              </Flex>

              <Text fontSize="sm" fontWeight={600} color="text.primary" mb={2}>
                {t('protocols.options', 'Options')}
              </Text>
              {stepOptions.map((opt, i) => (
                <Flex key={i} gap={2} mb={2} alignItems="center">
                  <Field.Root flex={1}>
                    <Field.Label
                      htmlFor={`report-field-option-input-${i}`}
                      srOnly
                    >
                      {t(
                        'protocols.optionLabelAria',
                        'Option {{number}} label',
                        { number: i + 1 },
                      )}
                    </Field.Label>
                    <InputField
                      id={`report-field-option-input-${i}`}
                      value={opt.label}
                      onChange={(e) =>
                        handleUpdateOption(i, 'label', e.target.value)
                      }
                      placeholder={t(
                        'protocols.optionLabelPlaceholder',
                        'Option label',
                      )}
                      size="sm"
                      data-e2e={`report-field-option-input-${i}`}
                    />
                  </Field.Root>
                  <Box
                    as="button"
                    p={1}
                    color="text.danger"
                    _hover={{ color: 'red.600' }}
                    onClick={() => handleRemoveOption(i)}
                    data-e2e={`remove-report-field-option-button-${i}`}
                  >
                    <RiCloseCircleLine size={16} />
                  </Box>
                </Flex>
              ))}
              <ButtonComponent
                visual="secondary"
                size="sm"
                onClick={handleAddOption}
                data-e2e="add-report-field-option-button"
              >
                <HiPlus size={14} />
                <Text ml={1}>{t('protocols.addOption', 'Add Option')}</Text>
              </ButtonComponent>
            </>
          )}

          {phase === 'intake' && (
            <Box
              mt={5}
              pt={4}
              borderTop="1px solid"
              borderColor="border.primary"
              data-e2e="parameter-declaration-section"
            >
              <Text fontSize="sm" fontWeight={600} color="text.primary" mb={2}>
                {t('protocols.parameterDeclaration', 'Parameter Declaration')}
              </Text>

              <Flex
                alignItems="center"
                gap={2}
                cursor="pointer"
                mb={4}
                onClick={() => setParamAnnounce(!paramAnnounce)}
              >
                <Box
                  w="16px"
                  h="16px"
                  borderRadius="sm"
                  border="2px solid"
                  borderColor={paramAnnounce ? 'brand.500' : 'border.secondary'}
                  bg={paramAnnounce ? 'brand.500' : 'transparent'}
                />
                <Text
                  fontSize="sm"
                  color="text.primary"
                  data-e2e="report-field-param-announce-checkbox"
                >
                  {t(
                    'protocols.announceParameter',
                    'Include in alert summary / announce display',
                  )}
                </Text>
              </Flex>

              <Field.Root mb={4}>
                <Field.Label htmlFor="report-field-announce-order-input">
                  {t('protocols.announceOrder', 'Announce Display Order')}
                </Field.Label>
                <InputField
                  id="report-field-announce-order-input"
                  type="number"
                  value={String(paramAnnounceOrder)}
                  onChange={(e) =>
                    setParamAnnounceOrder(Number(e.target.value) || 1)
                  }
                  data-e2e="report-field-announce-order-input"
                />
              </Field.Root>

              <Text fontSize="sm" fontWeight={600} color="text.primary" mb={1}>
                {t('protocols.requiredAtCreate', 'Required at Alert Creation')}
              </Text>
              <ToggleGroup
                options={[
                  {
                    value: 'none',
                    label: t('protocols.paramRequiredNone', 'None (Unprompted)'),
                    'data-e2e': 'param-req-none',
                  },
                  {
                    value: 'prompt',
                    label: t('protocols.paramRequiredPrompt', 'Prompt'),
                    'data-e2e': 'param-req-prompt',
                  },
                  {
                    value: 'require',
                    label: t('protocols.paramRequiredRequire', 'Require'),
                    'data-e2e': 'param-req-require',
                  },
                ]}
                value={paramRequiredAtCreate}
                onChange={(v) =>
                  setParamRequiredAtCreate(
                    v as 'require' | 'prompt' | 'none',
                  )
                }
                fillWidth
                data-e2e="report-field-required-at-create-toggle-group"
              />
            </Box>
          )}
        </Box>
      </DrawerComponent>
    </Box>
  );
}
